// auth.effects.ts
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, tap, switchMap, take } from 'rxjs/operators';
import { SiteService } from 'src/app/core/services/APIs/site/site.service';
import { 
  loadSiteSuccess, 
  loadSiteTypeSuccess, 
  loadSiteTypesSuccess, 
  loadSites, 
  loadSitesTypeSuccess,
  addSite,
  addSiteSuccess,
  addSiteFailure,
  editSite,
  editSiteSuccess,
  editSiteFailure,
  updateSiteLogo,
  updateSiteLogoSuccess,
  updateSiteLogoFailure
} from '../actions/site.actions';
import { Store } from '@ngrx/store';
import { AddAll, transformPayload } from 'src/app/core/utils/array-utils';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { selectCurrentLanguage } from '../selectors/language.selectors';
import { AppState } from '../app.store';
import { CustomToastService } from 'src/app/shared/services/custom-toast.service';
import { selectSites } from '../selectors/site.selectors';

@Injectable()
export class SiteEffects {
    skip = 0;
    limit = 250;
    
    constructor(private actions$: Actions,
        private store: Store<AppState>,
        private siteService: SiteService,
        private router: Router,
        private translateService: TranslateService,
        private customToast: CustomToastService) { }

    loadRoles$ = createEffect(
        () => this.actions$.pipe(
            ofType(loadSites),
            tap(() => {
                // Check localStorage first - only require sites data to be present
                const storedSites = localStorage.getItem('sites');
                
                if (storedSites) {
                    try {
                        const sitesData = JSON.parse(storedSites);
                        const sites = sitesData.sites;
                        
                        if (sites && sites.length > 0) {
                            // Translate sites before dispatching
                            const translatedSites = sites.map((site: any) => {
                                if (site.siteId === 'all') {
                                    return {
                                        ...site,
                                        name: this.translateService.instant('COMMON.ALL')
                                    };
                                }
                                return site;
                            });

                            this.store.dispatch(loadSitesTypeSuccess({ sites: translatedSites }));
                            
                            // Handle site selection if available
                            const storedSite = localStorage.getItem('site');
                            if (storedSite) {
                                try {
                                    const siteData = JSON.parse(storedSite);
                                    const site = siteData.site;
                                    
                                    // Translate single site if needed
                                    let translatedSite = site;
                                    if (site.siteId === 'all') {
                                        translatedSite = {
                                            ...site,
                                            name: this.translateService.instant('COMMON.ALL')
                                        };
                                    }
                                    
                                    this.store.dispatch(loadSiteSuccess({ site: translatedSite }));
                                } catch (error) {
                                    console.error('Error parsing stored site:', error);
                                    // Default to first site if parsing fails
                                    this.store.dispatch(loadSiteSuccess({ site: translatedSites[0] }));
                                }
                            } else {
                                // Default to first site if no stored site
                                this.store.dispatch(loadSiteSuccess({ site: translatedSites[0] }));
                            }
                            
                            // Handle types if available
                            const storedTypes = localStorage.getItem('types');
                            if (storedTypes) {
                                try {
                                    const typesData = JSON.parse(storedTypes);
                                    const typesSite = typesData.typesSite;
                                    this.store.dispatch(loadSiteTypesSuccess({ typesSite }));
                                    
                                    // Handle type selection if available
                                    const storedType = localStorage.getItem('type');
                                    if (storedType) {
                                        try {
                                            const typeData = JSON.parse(storedType);
                                            const typeSite = typeData.typeSite;
                                            this.store.dispatch(loadSiteTypeSuccess({ typeSite }));
                                        } catch (error) {
                                            console.error('Error parsing stored type:', error);
                                            // Default to first type if parsing fails
                                            if (typesSite && typesSite.length > 0) {
                                                this.store.dispatch(loadSiteTypeSuccess({ typeSite: typesSite[0] }));
                                            }
                                        }
                                    } else {
                                        // Default to first type if no stored type
                                        if (typesSite && typesSite.length > 0) {
                                            this.store.dispatch(loadSiteTypeSuccess({ typeSite: typesSite[0] }));
                                        }
                                    }
                                } catch (error) {
                                    console.error('Error parsing stored types:', error);
                                }
                            }

                            return;
                        }
                    } catch (error: any) {
                        console.error('Error parsing stored sites data', error);
                        this.customToast.showErrorModal(
                            error?.error?.error || this.translateService.instant('COMMON.ERRORS.DATA_PARSING_FAILED'),
                            this.translateService.instant('COMMON.ERROR')
                        );
                    }
                }

                // If sites not in localStorage or parsing failed, fetch from API
                console.log('Fetching sites from API...');
                this.fetchSitesFromAPI();
            })
        ),
        { dispatch: false }
    );

    // New effect for syncing sites with API - REMOVED to prevent loops
    // syncSitesWithAPI$ = createEffect(() => ... );

    // Add site effect
    addSite$ = createEffect(() =>
        this.actions$.pipe(
            ofType(addSite),
            switchMap(({ siteData }) =>
                this.siteService.registerSite(siteData).pipe(
                    map((response) => addSiteSuccess({ site: response })),
                    catchError((error) => {
                        this.customToast.showErrorModal(
                            error?.error?.error || this.translateService.instant('COMMON.ERRORS.SITE_ADD_FAILED'),
                            this.translateService.instant('COMMON.ERROR')
                        );
                        return of(addSiteFailure({ error }));
                    })
                )
            )
        )
    );

    // Edit site effect
    editSite$ = createEffect(() =>
        this.actions$.pipe(
            ofType(editSite),
            switchMap(({ siteData }) =>
                this.siteService.updateSite(siteData).pipe(
                    map((response) => editSiteSuccess({ site: response })),
                    catchError((error) => {
                        this.customToast.showErrorModal(
                            error?.error?.error || this.translateService.instant('COMMON.ERRORS.SITE_UPDATE_FAILED'),
                            this.translateService.instant('COMMON.ERROR')
                        );
                        return of(editSiteFailure({ error }));
                    })
                )
            )
        )
    );

    // Update site logo effect
    updateSiteLogo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateSiteLogo),
            switchMap(({ logoData }) =>
                this.siteService.uploadSiteLogo(logoData).pipe(
                    map((response) => updateSiteLogoSuccess({ 
                        siteId: logoData.siteId, 
                        logoUrl: response.logoUrl || logoData.logo 
                    })),
                    catchError((error) => {
                        this.customToast.showErrorModal(
                            error?.error?.error || this.translateService.instant('COMMON.ERRORS.LOGO_UPLOAD_FAILED'),
                            this.translateService.instant('COMMON.ERROR')
                        );
                        return of(updateSiteLogoFailure({ error }));
                    })
                )
            )
        )
    );

    // Success effects for navigation and localStorage updates
    addSiteSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(addSiteSuccess),
            tap(({ site }) => {
                this.customToast.showSuccess(this.translateService.instant('COMMON.SUCCESS.SITE_ADDED'));
                this.router.navigate(['/dashboard/sites']);
                // Update localStorage with the new site
                this.updateLocalStorageAfterSiteChange();
            })
        ),
        { dispatch: false }
    );

    editSiteSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(editSiteSuccess),
            tap(({ site }) => {
                this.customToast.showSuccess(this.translateService.instant('COMMON.SUCCESS.SITE_UPDATED'));
                this.router.navigate(['/dashboard/sites']);
                // Update localStorage with the edited site
                this.updateLocalStorageAfterSiteChange();
            })
        ),
        { dispatch: false }
    );

    updateSiteLogoSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateSiteLogoSuccess),
            tap(({ siteId, logoUrl }) => {
                this.customToast.showSuccess(this.translateService.instant('COMMON.SUCCESS.LOGO_UPLOADED'));
                this.updateLocalStorageAfterLogoChange(siteId, logoUrl);
            })
        ),
        { dispatch: false }
    );

    persistSites$ = createEffect(()=>
        this.actions$.pipe(
            ofType(loadSitesTypeSuccess),
            tap(({sites})=>{
                // Translate 'all' site if it exists in the array
                const translatedSites = sites.map(site => {
                    if (site.siteId === 'all') {
                        return {
                            ...site,
                            name: this.translateService.instant('COMMON.ALL')
                        };
                    }
                    return site;
                });
                localStorage.setItem('sites', JSON.stringify({ sites: translatedSites }));
                
                // Check if there's an existing site in localStorage
                const storedSite = localStorage.getItem('site');
                if (storedSite) {
                    const existingSite = JSON.parse(storedSite).site;
                    // Find the site in the new translated sites array
                    const matchingSite = translatedSites.find(site => site.siteId === existingSite.siteId);
                    if (matchingSite) {
                        this.store.dispatch(loadSiteSuccess({ site: matchingSite }));
                        return;
                    }
                }
                // If no stored site or matching site found, default to first site
                this.store.dispatch(loadSiteSuccess({ site: translatedSites[0] })); 
            })
        ),
        { dispatch: false }
    )
    persistSite$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(loadSiteSuccess),
                tap(({ site }) => { 
                    // If siteId is 'all', translate the name before storing
                    if (site.siteId === 'all') {
                        const translatedSite = {
                            ...site,
                            name: this.translateService.instant('COMMON.ALL')
                        };
                        localStorage.setItem('site', JSON.stringify({ site: translatedSite })); 
                        localStorage.removeItem('types');
                        localStorage.removeItem('type');
                        this.router.navigate(['/dashboard/organizations']);
                        return;
                    }
                    
                    localStorage.setItem('site', JSON.stringify({ site })); 
                    let arrayOfTypes = transformPayload(site.registeredServices);
                    this.store.dispatch(loadSiteTypesSuccess({typesSite:arrayOfTypes}))
                })
            ),
        { dispatch: false }
    );
    persistTypes$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(loadSiteTypesSuccess),
                tap(({ typesSite}) => {
                    localStorage.setItem('types', JSON.stringify({ typesSite })); 
                    
                    // Check if type is already in local storage
                    const storedType = localStorage.getItem('type');
                    const parsedStoredType = storedType ? JSON.parse(storedType).typeSite : null;
                    
                    if (!storedType || !typesSite.includes(parsedStoredType)) {
                        // Dispatch first type if there's no stored type or if stored type isn't in typesSite
                        this.store.dispatch(loadSiteTypeSuccess({typeSite: typesSite[0]}))
                    }
                })
            ),
        { dispatch: false }
    );
    persistType$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(loadSiteTypeSuccess),
                tap(({ typeSite}) => {
                    localStorage.setItem('type', JSON.stringify({ typeSite })); 
                })
            ),
        { dispatch: false }
    );

    // Update language change effect to handle both sites and site
    updateAllSiteOnLanguageChange$ = createEffect(
        () => this.store.select(selectCurrentLanguage).pipe(
            tap((language) => {
                // Handle single site
                const storedSite = localStorage.getItem('site');
                if (storedSite) {
                    const site = JSON.parse(storedSite).site;
                    if (site.siteId === 'all') {
                        const translatedSite = {
                            ...site,
                            name: this.translateService.instant('COMMON.ALL')
                        };
                        localStorage.setItem('site', JSON.stringify({ site: translatedSite }));
                        this.store.dispatch(loadSiteSuccess({ site: translatedSite }));
                    }
                }

                // Handle sites array
                const storedSites = localStorage.getItem('sites');
                if (storedSites) {
                    const sites = JSON.parse(storedSites).sites;
                    const translatedSites = sites.map((site: any) => {
                        if (site.siteId === 'all') {
                            return {
                                ...site,
                                name: this.translateService.instant('COMMON.ALL')
                            };
                        }
                        return site;
                    });
                    localStorage.setItem('sites', JSON.stringify({ sites: translatedSites }));
                    this.store.dispatch(loadSitesTypeSuccess({ sites: translatedSites }));
                }
            })
        ),
        { dispatch: false }
    );

    // Helper methods for localStorage updates
    private updateLocalStorageAfterSiteChange(): void {
        // Get current sites from store and update localStorage
        this.store.select(selectSites).pipe(
            take(1) // Only take the current value, don't subscribe
        ).subscribe((sites: any[]) => {
            if (sites && sites.length > 0) {
                const translatedSites = sites.map((site: any) => {
                    if (site.siteId === 'all') {
                        return {
                            ...site,
                            name: this.translateService.instant('COMMON.ALL')
                        };
                    }
                    return site;
                });
                localStorage.setItem('sites', JSON.stringify({ sites: translatedSites }));
            }
        });
    }

    private updateLocalStorageAfterLogoChange(siteId: string, logoUrl: string): void {
        // Update sites in localStorage
        const storedSites = localStorage.getItem('sites');
        if (storedSites) {
            try {
                const sitesData = JSON.parse(storedSites);
                const updatedSites = sitesData.sites.map((site: any) => 
                    site.siteId === siteId ? { ...site, logoUrl } : site
                );
                localStorage.setItem('sites', JSON.stringify({ sites: updatedSites }));
            } catch (error) {
                console.error('Error updating sites in localStorage after logo change:', error);
            }
        }

        // Update single site in localStorage if it matches
        const storedSite = localStorage.getItem('site');
        if (storedSite) {
            try {
                const siteData = JSON.parse(storedSite);
                if (siteData.site.siteId === siteId) {
                    const updatedSite = { ...siteData.site, logoUrl };
                    localStorage.setItem('site', JSON.stringify({ site: updatedSite }));
                }
            } catch (error) {
                console.error('Error updating site in localStorage after logo change:', error);
            }
        }
    }

    // New helper methods for fetching and comparing sites
    private fetchSitesFromAPI(): void {
        this.siteService.getSites({ limit: this.limit, skip: this.skip }, true)
            .pipe(
                map((sites: any) => {
                    const siteData = sites?.data || sites;
                    // Add "All" site and translate sites from API
                    const allSite = {
                        siteId: 'all',
                        name: this.translateService.instant('COMMON.ALL')
                    };
                    return AddAll(siteData, allSite);
                }),
                catchError((error) => {
                    this.customToast.showErrorModal(
                        error?.error?.error || this.translateService.instant('COMMON.ERRORS.SITES_LOADING_FAILED'),
                        this.translateService.instant('COMMON.ERROR')
                    );
                    return of({ type: '[Site] Load Sites Failed' });
                })
            )
            .subscribe((sites: any) => {
                this.store.dispatch(loadSitesTypeSuccess({ sites: sites }));
            });
    }
}
